// Cognito auth management — all operations go through Lambda (client_secret stays server-side)
import { lambda, API_USER_AGENT, setSecureCookie, removeCookie } from './Functions';
import { sanitizeErrorMessage } from './utils/sanitizeError';

const TOKEN_KEY = 'gbx_tokens';
const AUTH_EVENT = 'gbx_authStateChanged';

// -------------------------------------------------------
// Token storage (localStorage for persistence across tabs)
// -------------------------------------------------------
function saveTokens(tokens) {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  } catch {}
}

function getTokens() {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_KEY));
  } catch {
    return null;
  }
}

function clearTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

function decodeJwtPayload(token) {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/** Resolve profile names from Cognito/Google claims, falling back to the mapped `name` claim. */
function getNamePartsFromToken(decoded) {
  const givenName = String(decoded?.given_name || '').trim();
  const familyName = String(decoded?.family_name || '').trim();
  if (givenName || familyName) {
    return { firstName: givenName, lastName: familyName };
  }

  const fullName = String(decoded?.name || '').trim();
  if (!fullName) return { firstName: '', lastName: '' };
  const parts = fullName.split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
}

function dispatchAuthEvent() {
  window.dispatchEvent(new Event(AUTH_EVENT));
}

/** Cart and other listeners use `gbx_authStateChanged`; Navbar/subscription also use `authStateChanged`. Call after session-affecting steps (e.g. cart transfer post–sign-in). */
export function notifySessionChanged() {
  dispatchAuthEvent();
  window.dispatchEvent(new Event('authStateChanged'));
}

// -------------------------------------------------------
// Public API
// -------------------------------------------------------

/** Get the current Cognito ID token (auto-refreshes if expiring). */
export async function getAuthToken() {
  const tokens = getTokens();
  if (!tokens?.idToken) return null;

  const payload = decodeJwtPayload(tokens.idToken);
  const now = Math.floor(Date.now() / 1000);

  if (payload?.exp && payload.exp < now + 60) {
    const refreshed = await refreshTokens();
    return refreshed?.idToken || null;
  }
  return tokens.idToken;
}

/** True when a valid session exists. */
export async function isUserAuthenticated() {
  const token = await getAuthToken();
  return !!token && !!sessionStorage.getItem('gbx_data');
}

/** Get user info from the current ID token (sub, email, given_name, etc.) */
export function getCurrentUserFromToken() {
  const tokens = getTokens();
  if (!tokens?.idToken) return null;
  return decodeJwtPayload(tokens.idToken);
}

/** Normalize DB/session hasPassword flags for comparison. */
function normalizedHasPasswordFlag(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  return null;
}

/**
 * Merge `hasPassword` from profile API into session + localStorage.
 * Dispatches auth events only when the flag actually changes — avoids infinite loops
 * (e.g. Settings + Cart/Subscription listeners re-firing sync).
 */
export function mergeAuthProfileIntoSession(profile) {
  if (!profile || typeof profile.hasPassword !== 'boolean') return;
  const hp = !!profile.hasPassword;
  const stored = sessionStorage.getItem('gbx_data');
  if (!stored) {
    try {
      localStorage.setItem('gbx_has_password', String(hp));
    } catch (_) {}
    return;
  }
  let parsed;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return;
  }
  const beforeNorm = normalizedHasPasswordFlag(parsed.hasPassword);
  const unchanged = beforeNorm === hp;
  parsed.hasPassword = hp;
  try {
    localStorage.setItem('gbx_has_password', String(hp));
  } catch (_) {}
  if (unchanged) return;
  try {
    sessionStorage.setItem('gbx_data', JSON.stringify(parsed));
  } catch (_) {}
  dispatchAuthEvent();
  window.dispatchEvent(new Event('authStateChanged'));
}

/** GET /giftbox/auth/me — returns JSON or null if unavailable / unauthenticated. */
export async function fetchAuthProfile() {
  const token = await getAuthToken();
  if (!token) return null;
  try {
    const resp = await fetch(`${lambda}/giftbox/auth/me`, {
      method: 'GET',
      headers: { 'User-Agent': API_USER_AGENT, Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) return null;
    return await resp.json();
  } catch {
    return null;
  }
}

/** Refresh `hasPassword` from server into session (non-throwing). */
export async function syncAuthProfileHasPassword() {
  const data = await fetchAuthProfile();
  mergeAuthProfileIntoSession(data);
  return data;
}

// -------------------------------------------------------
// Sign up → Lambda /giftbox/auth/signup (returns needsConfirmation; no tokens yet)
// -------------------------------------------------------
export async function signUp(email, password, firstName, lastName) {
  const resp = await fetch(`${lambda}/giftbox/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ email, password, firstName, lastName }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    const err = new Error(sanitizeErrorMessage(data.error) || 'Sign up failed');
    err.code = data?.code;
    throw err;
  }
  return data;
}

// -------------------------------------------------------
// Confirm sign up → Lambda /giftbox/auth/confirm-signup (then tokens if password sent)
// -------------------------------------------------------
export async function confirmSignUp(email, code, password, firstName, lastName) {
  const resp = await fetch(`${lambda}/giftbox/auth/confirm-signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({
      email,
      code,
      ...(password ? { password } : {}),
      ...(firstName != null ? { firstName } : {}),
      ...(lastName != null ? { lastName } : {}),
    }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(sanitizeErrorMessage(data.error) || 'Confirmation failed');

  if (data.idToken) {
    saveTokens({ idToken: data.idToken, accessToken: data.accessToken, refreshToken: data.refreshToken, email });
    dispatchAuthEvent();
  }
  return data;
}

// -------------------------------------------------------
// Resend signup confirmation code
// -------------------------------------------------------
export async function resendConfirmationCode(email) {
  const resp = await fetch(`${lambda}/giftbox/auth/resend-confirmation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ email }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(sanitizeErrorMessage(data.error) || 'Failed to resend code');
  return data;
}

// -------------------------------------------------------
// Sign in → Lambda /giftbox/auth/signin
// -------------------------------------------------------
export async function signIn(email, password) {
  const resp = await fetch(`${lambda}/giftbox/auth/signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ email, password }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    const err = new Error(
      data?.code === 'USER_BLOCKED'
        ? 'Your email is blocked, please contact support'
        : sanitizeErrorMessage(data.error) || 'Sign in failed'
    );
    err.code = data?.code;
    throw err;
  }

  if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
    return data;
  }

  if (data.idToken) {
    saveTokens({ idToken: data.idToken, accessToken: data.accessToken, refreshToken: data.refreshToken, email });
    dispatchAuthEvent();
  }
  return data;
}

// -------------------------------------------------------
// Respond to NEW_PASSWORD_REQUIRED challenge
// -------------------------------------------------------
export async function respondToNewPasswordChallenge(session, email, newPassword) {
  const resp = await fetch(`${lambda}/giftbox/auth/new-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ session, email, newPassword }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(sanitizeErrorMessage(data.error) || 'Failed to set new password');

  if (data.idToken) {
    saveTokens({ idToken: data.idToken, accessToken: data.accessToken, refreshToken: data.refreshToken, email });
    dispatchAuthEvent();
  }
  return data;
}

// -------------------------------------------------------
// Sign out (local only — Cognito tokens simply expire)
// -------------------------------------------------------
export function signOut() {
  clearTokens();
  sessionStorage.removeItem('gbx_data');
  removeCookie('gbx_id');
  try { localStorage.removeItem('gbx_has_password'); } catch (_) {}
  sessionStorage.setItem('explicit_signout', 'true');
  dispatchAuthEvent();
}

// -------------------------------------------------------
// Forgot password → Lambda /giftbox/auth/forgot-password
// -------------------------------------------------------
export async function forgotPassword(email) {
  const resp = await fetch(`${lambda}/giftbox/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ email }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(sanitizeErrorMessage(data.error) || 'Failed to send verification code');
  return data;
}

// -------------------------------------------------------
// Confirm forgot password → Lambda /giftbox/auth/confirm-forgot-password
// -------------------------------------------------------
export async function confirmForgotPassword(email, code, newPassword) {
  const resp = await fetch(`${lambda}/giftbox/auth/confirm-forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ email, code, newPassword }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(sanitizeErrorMessage(data.error) || 'Failed to reset password');
  return data;
}

// -------------------------------------------------------
// Refresh tokens → Lambda /giftbox/auth/refresh
// -------------------------------------------------------
export async function refreshTokens() {
  const tokens = getTokens();
  if (!tokens?.refreshToken || !tokens?.email) {
    clearTokens();
    return null;
  }
  try {
    const resp = await fetch(`${lambda}/giftbox/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
      body: JSON.stringify({ refreshToken: tokens.refreshToken, email: tokens.email }),
    });
    if (!resp.ok) {
      clearTokens();
      dispatchAuthEvent();
      return null;
    }
    const data = await resp.json();
    saveTokens({
      idToken: data.idToken,
      accessToken: data.accessToken,
      refreshToken: tokens.refreshToken,
      email: tokens.email,
    });
    return data;
  } catch {
    clearTokens();
    dispatchAuthEvent();
    return null;
  }
}

// -------------------------------------------------------
// Store tokens received from force-create or external flow
// -------------------------------------------------------
export function storeTokens({ idToken, accessToken, refreshToken, email }) {
  if (idToken) {
    saveTokens({ idToken, accessToken, refreshToken, email });
    dispatchAuthEvent();
  }
}

const COGNITO_DOMAIN = (
  process.env.REACT_APP_COGNITO_DOMAIN ||
  'eu-central-1oqfbjntli.auth.eu-central-1.amazoncognito.com'
).replace(/^https?:\/\//, '');
const COGNITO_CLIENT_ID =
  process.env.REACT_APP_COGNITO_CLIENT_ID || '56af0c4kgt6r3fah46c67da1vi';
const OAUTH_STATE_KEY = 'gbx_oauth_state';

/** Redirect URI for Cognito Hosted UI (must match app client callback URLs). */
export function getCognitoOAuthRedirectUri() {
  if (typeof window === 'undefined') return 'https://giftbox.mobi/signin';
  return `${window.location.origin}/signin`;
}

function buildCognitoAuthorizeUrl(identityProvider, redirectUri) {
  const uri = redirectUri || getCognitoOAuthRedirectUri();
  const state =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
  } catch (_) {}
  const params = new URLSearchParams({
    client_id: COGNITO_CLIENT_ID,
    response_type: 'code',
    scope: 'email openid phone profile',
    redirect_uri: uri,
    identity_provider: identityProvider,
    state,
  });
  return `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

function startCognitoHostedSignIn(identityProvider, redirectUri) {
  const url = buildCognitoAuthorizeUrl(identityProvider, redirectUri);
  if (typeof window !== 'undefined') {
    const target = window.top || window;
    target.location.href = url;
  }
}

/** Cognito authorize URL that goes straight to Google. */
export function getGoogleCognitoAuthorizeUrl(redirectUri) {
  return buildCognitoAuthorizeUrl('Google', redirectUri);
}

/** Start Cognito Hosted UI Google sign-in (top-level redirect). */
export function startGoogleCognitoSignIn(redirectUri) {
  startCognitoHostedSignIn('Google', redirectUri);
}

/** Cognito authorize URL that goes straight to Sign in with Apple. */
export function getAppleCognitoAuthorizeUrl(redirectUri) {
  return buildCognitoAuthorizeUrl('SignInWithApple', redirectUri);
}

/** Start Cognito Hosted UI Apple sign-in (top-level redirect). */
export function startAppleCognitoSignIn(redirectUri) {
  startCognitoHostedSignIn('SignInWithApple', redirectUri);
}

// -------------------------------------------------------
// Sign in with Google (GIS id_token) → Lambda /giftbox/auth/google (legacy)
// -------------------------------------------------------
export async function signInWithGoogle(idToken) {
  const resp = await fetch(`${lambda}/giftbox/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ id_token: idToken }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    const err = new Error(
      data?.code === 'USER_BLOCKED'
        ? 'Your email is blocked, please contact support'
        : sanitizeErrorMessage(data.error || data.message) || 'Google sign-in failed'
    );
    err.code = data?.code;
    throw err;
  }
  if (data.idToken) {
    saveTokens({
      idToken: data.idToken,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      email: data.email,
    });
    dispatchAuthEvent();
  }
  return data;
}

// -------------------------------------------------------
// Cognito Hosted UI — exchange authorization code → Lambda /giftbox/auth/cognito-oauth
// -------------------------------------------------------
export async function exchangeCognitoAuthCode(code, redirectUri) {
  const uri = redirectUri || getCognitoOAuthRedirectUri();
  const resp = await fetch(`${lambda}/giftbox/auth/cognito-oauth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ code, redirect_uri: uri }),
  });
  const data = await resp.json();
  if (!resp.ok) {
    const err = new Error(
      data?.code === 'USER_BLOCKED'
        ? 'Your email is blocked, please contact support'
        : data?.code === 'REQUIRES_EMAIL_SIGN_IN'
          ? sanitizeErrorMessage(data.error) ||
            'This email is already registered with a password. Please sign in with your email and password.'
          : sanitizeErrorMessage(data.error || data.message) || 'Social sign-in failed'
    );
    err.code = data?.code;
    throw err;
  }
  if (data.idToken) {
    saveTokens({
      idToken: data.idToken,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      email: data.email,
    });
    dispatchAuthEvent();
  }
  return data;
}

/** Apply Cognito OAuth result to session (cookies + gbx_data). */
export function applyOAuthSession(data) {
  const decoded = getCurrentUserFromToken();
  const { firstName, lastName } = getNamePartsFromToken(decoded);
  const sessionData = {
    user: {
      id: decoded?.sub || data?.sub,
      email: decoded?.email || data?.email,
      first_name: firstName,
      last_name: lastName,
      name: [firstName, lastName].filter(Boolean).join(' '),
    },
    cognito_sub: decoded?.sub || data?.sub,
    email: decoded?.email || data?.email,
    hasPassword: data?.hasPassword,
  };
  if (data?.hasPassword !== undefined) {
    try {
      localStorage.setItem('gbx_has_password', String(data.hasPassword));
    } catch (_) {}
  }
  setSecureCookie('gbx_id', decoded?.sub || data?.sub || '', 7);
  sessionStorage.setItem('gbx_data', JSON.stringify(sessionData));
  window.dispatchEvent(new Event('authStateChanged'));
  return sessionData;
}

export function consumeOAuthState(stateFromUrl) {
  let expected = null;
  try {
    expected = sessionStorage.getItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
  } catch (_) {}
  if (!expected || !stateFromUrl) return true;
  return expected === stateFromUrl;
}

// -------------------------------------------------------
// Sign in with Apple (authorization code) → Lambda /giftbox/auth/apple
// -------------------------------------------------------
export async function signInWithApple(code, redirectUri) {
  const uri = redirectUri || `${window.location.origin}/auth/apple-callback`;
  const resp = await fetch(`${lambda}/giftbox/auth/apple`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'User-Agent': API_USER_AGENT },
    body: JSON.stringify({ code, redirect_uri: uri }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(sanitizeErrorMessage(data.error || data.message) || 'Apple sign-in failed');
  if (data.idToken) {
    saveTokens({
      idToken: data.idToken,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      email: data.email,
    });
    dispatchAuthEvent();
  }
  return data;
}

// -------------------------------------------------------
// Auth state listener (replaces Firebase onAuthStateChanged)
// -------------------------------------------------------
export function onAuthStateChanged(callback) {
  const handle = () => {
    const tokens = getTokens();
    callback(tokens?.idToken ? getCurrentUserFromToken() : null);
  };
  window.addEventListener(AUTH_EVENT, handle);
  window.addEventListener('storage', (e) => {
    if (e.key === TOKEN_KEY) handle();
  });
  handle();
  return () => {
    window.removeEventListener(AUTH_EVENT, handle);
  };
}

// -------------------------------------------------------
// Auto-login: restore session from stored refresh token
// -------------------------------------------------------
export async function autoLogin() {
  const explicitSignout = sessionStorage.getItem('explicit_signout');
  if (explicitSignout === 'true') {
    sessionStorage.removeItem('explicit_signout');
    return;
  }

  const tokens = getTokens();
  if (!tokens?.idToken) return;

  const payload = decodeJwtPayload(tokens.idToken);
  const now = Math.floor(Date.now() / 1000);

  if (payload?.exp && payload.exp < now + 60) {
    const refreshed = await refreshTokens();
    if (!refreshed) return;
  }

  const data = sessionStorage.getItem('gbx_data');
  if (!data) {
    const decoded = getCurrentUserFromToken();
    if (decoded) {
      const { firstName, lastName } = getNamePartsFromToken(decoded);
      let hasPassword;
      try {
        const stored = localStorage.getItem('gbx_has_password');
        if (stored === 'true') hasPassword = true;
        else if (stored === 'false') hasPassword = false;
      } catch (_) {}
      const sessionData = {
        user: {
          id: decoded.sub,
          email: decoded.email,
          first_name: firstName,
          last_name: lastName,
          name: [firstName, lastName].filter(Boolean).join(' '),
        },
        cognito_sub: decoded.sub,
        email: decoded.email,
        ...(hasPassword !== undefined && { hasPassword }),
      };
      sessionStorage.setItem('gbx_data', JSON.stringify(sessionData));
      setSecureCookie('gbx_id', decoded.sub, 7);
    }
  } else {
    const parsed = JSON.parse(data);
    const decoded = getCurrentUserFromToken();
    const { firstName, lastName } = getNamePartsFromToken(decoded);
    if (
      decoded &&
      (!parsed.user?.first_name || !parsed.user?.last_name) &&
      (firstName || lastName)
    ) {
      parsed.user = {
        ...parsed.user,
        first_name: parsed.user?.first_name || firstName,
        last_name: parsed.user?.last_name || lastName,
        name: [parsed.user?.first_name || firstName, parsed.user?.last_name || lastName]
          .filter(Boolean)
          .join(' '),
      };
      sessionStorage.setItem('gbx_data', JSON.stringify(parsed));
    }
    setSecureCookie('gbx_id', parsed.cognito_sub || parsed.firebase_uid || parsed.user?.id || '', 7);
  }

  syncAuthProfileHasPassword().catch(() => {});
}
